import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Users, Clock, BookOpen, GraduationCap, UserCheck, UserPlus,
  Calendar, CheckCircle2, Lock, Sparkles, ArrowRight, Heart,
  ChevronLeft, ChevronRight, Search, Plus, Menu, LayoutDashboard, CornerUpRight, PartyPopper
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useSetupStatus, ADMIN_SETUP_PATH_TO_ETAPA } from '../../hooks/useSetupStatus';
import type { EtapaSetup } from '../../hooks/useSetupStatus';
import { useAuthStore } from '../../store/authStore';
import { useWizard } from '../../api/hooks/useWizard';
import { api } from '../../api/client';
import { toast } from 'sonner';
import { ConfirmModal } from '../ui/ConfirmModal';
import { SetupMobileHandoff } from './SetupMobileHandoff';
import { GradeAssistant } from '../grade/GradeAssistant';
import './SetupWizard.css';

import { EquipePage } from '../../pages/admin/Equipe';
import { TurnosPage } from '../../pages/admin/Turnos';
import { MateriasPage } from '../../pages/admin/Materias';
import { ProfessoresPage } from '../../pages/admin/Professores';
import { TurmasPage } from '../../pages/admin/Turmas';
import { ResponsaveisPage } from '../../pages/admin/Responsaveis';
import { AlunosPage } from '../../pages/admin/Alunos';

const ICONE_MAP: Record<string, LucideIcon> = {
  users: Users,
  clock: Clock,
  bookOpen: BookOpen,
  graduationCap: GraduationCap,
  userCheck: UserCheck,
  userPlus: UserPlus,
  calendar: Calendar,
  heart: Heart,
};

function deckPillLabel(etapa: EtapaSetup, isAtual: boolean): string {
  if (etapa.status === 'concluida') return 'Concluído';
  if (isAtual) return etapa.status === 'parcial' ? 'Em andamento' : 'Próximo';
  if (etapa.status === 'bloqueada') return 'Bloqueado';
  return 'Pendente';
}

const NOME_CURTO: Record<string, string> = {
  equipe: 'Equipe',
  turnos: 'Turnos',
  materias: 'Matérias',
  professores: 'Professores',
  turmas: 'Turmas',
  responsaveis: 'Responsáveis',
  alunos: 'Alunos',
  grade: 'Grade',
};

/** Uma linha — card azul enxuto */
const ETAPA_RESUMO: Record<string, { titulo: string; linha: string; btn: string }> = {
  equipe: {
    titulo: 'Monte a equipe inicial da escola',
    linha: 'Cadastre ao menos um membro (coordenação ou secretaria) para liberar a configuração dos turnos.',
    btn: 'Adicionar membro',
  },
  turnos: {
    titulo: 'Defina os turnos de funcionamento',
    linha: 'Configure manhã, tarde e noite para estruturar as turmas.',
    btn: 'Configurar turnos',
  },
  materias: {
    titulo: 'Cadastre as matérias escolares',
    linha: 'Adicione as disciplinas que serão ministradas aos alunos.',
    btn: 'Cadastrar matérias',
  },
  professores: {
    titulo: 'Cadastre o corpo docente',
    linha: 'Adicione os professores e vincule-os às matérias correspondentes.',
    btn: 'Adicionar professores',
  },
  turmas: {
    titulo: 'Crie as turmas e salas',
    linha: 'Defina as turmas do ano letivo e associe-as aos turnos.',
    btn: 'Criar turmas',
  },
  responsaveis: {
    titulo: 'Cadastre pais e responsáveis',
    linha: 'Registre contatos familiares para viabilizar os vínculos dos alunos.',
    btn: 'Cadastrar responsáveis',
  },
  alunos: {
    titulo: 'Matricule os alunos',
    linha: 'Vincule os alunos às turmas criadas e associe-os aos responsáveis.',
    btn: 'Matricular alunos',
  },
  grade: {
    titulo: 'Monte a grade horária semanal',
    linha: 'Distribua as matérias e professores nas turmas usando a auto-distribuição inteligente e salve para liberar o sistema.',
    btn: 'Concluir implantação',
  },
};

const MOCK_BROWSER_TITLES: Record<string, string> = {
  equipe: 'Equipe Administrativa',
  turnos: 'Turnos de Aulas',
  materias: 'Disciplinas da Escola',
  professores: 'Professores da Escola',
  turmas: 'Turmas e Salas',
  responsaveis: 'Cadastro de Responsáveis',
  alunos: 'Matrícula de Alunos',
  grade: 'Grade Horária Semanal',
};

function formatMetaText(id: string, total: number, meta: number, status: string): string {
  if (status === 'bloqueada' && id !== 'grade') {
    return 'Aguardando';
  }
  const plural = meta > 1;
  switch (id) {
    case 'equipe':
      return `${total} de ${meta} ${plural ? 'cadastrados' : 'cadastrado'}`;
    case 'turnos':
      return `${total} de ${meta} ${plural ? 'configurados' : 'configurado'}`;
    case 'materias':
      return `${total} de ${meta} ${plural ? 'cadastradas' : 'cadastrada'}`;
    case 'professores':
      return `${total} de ${meta} ${plural ? 'cadastrados' : 'cadastrado'}`;
    case 'turmas':
      return `${total} de ${meta} ${plural ? 'criadas' : 'criada'}`;
    case 'responsaveis':
      return `${total} de ${meta} ${plural ? 'cadastrados' : 'cadastrado'}`;
    case 'alunos':
      return `${total} de ${meta} ${plural ? 'vinculados' : 'vinculado'}`;
    case 'grade':
      return total > 0 ? 'Grade montada' : 'Não iniciada';
    default:
      return `${total}/${meta}`;
  }
}

function MockupContent({ stepId }: { stepId: string }) {
  if (stepId === 'equipe') {
    return (
      <div className="flex flex-col gap-4 h-full text-white/90 text-xs select-none">
        {/* Mock Header */}
        <div className="flex items-center justify-between border-b border-white/10 pb-2">
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 w-44">
            <Search size={12} className="text-white/40" />
            <span className="text-[10px] text-white/40">Buscar membro...</span>
          </div>
          <div className="px-3 py-1.5 bg-blue-600 rounded-lg font-bold text-[10px] flex items-center gap-1">
            <Plus size={10} /> Adicionar Membro
          </div>
        </div>
        {/* Cards Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-white/5 border border-white/10 rounded-xl flex flex-col gap-2 relative">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 font-bold flex items-center justify-center text-[10px]">
                ED
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-[11px]">Euler Diretor</span>
                <span className="text-[9px] text-white/50">Diretor(a)</span>
              </div>
            </div>
            <span className="absolute top-2 right-2 text-[8px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-bold">Ativo</span>
          </div>

          <div className="p-3 bg-white/5 border border-white/10 rounded-xl flex flex-col gap-2 relative">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 font-bold flex items-center justify-center text-[10px]">
                MC
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-[11px] text-white/40">Coordenador(a)</span>
                <span className="text-[9px] text-white/30">Aguardando cadastro</span>
              </div>
            </div>
            <span className="absolute top-2 right-2 text-[8px] px-1.5 py-0.5 rounded bg-white/5 text-white/40 font-bold">Pendente</span>
          </div>
        </div>
      </div>
    );
  }

  if (stepId === 'turnos') {
    return (
      <div className="flex flex-col gap-3 h-full text-white/90 text-xs select-none">
        <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 border-b border-white/10 pb-1">
          Configuração de Turnos
        </div>
        <div className="flex flex-col gap-2">
          {[
            { nome: 'Manhã', hora: '07:00 às 12:00', status: 'Ativo', color: 'border-blue-500/30 bg-blue-500/10' },
            { nome: 'Tarde', hora: '13:00 às 18:00', status: 'Ativo', color: 'border-emerald-500/30 bg-emerald-500/10' },
            { nome: 'Noite', hora: '19:00 às 22:30', status: 'Pendente', color: 'border-white/10 bg-white/5 opacity-50' },
          ].map((t, i) => (
            <div key={i} className={`p-2.5 border rounded-xl flex items-center justify-between ${t.color}`}>
              <div className="flex items-center gap-2.5">
                <Clock size={12} className="text-white/60" />
                <div className="flex flex-col">
                  <span className="font-bold text-[11px]">{t.nome}</span>
                  <span className="text-[9px] text-white/50">{t.hora}</span>
                </div>
              </div>
              <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${t.status === 'Ativo' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-white/40'}`}>
                {t.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (stepId === 'materias') {
    return (
      <div className="flex flex-col gap-3 h-full text-white/90 text-xs select-none">
        <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 border-b border-white/10 pb-1">
          Catálogo de Disciplinas
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { n: 'Matemática', sigla: 'MAT', color: 'bg-blue-500/20 text-blue-300' },
            { n: 'Português', sigla: 'PORT', color: 'bg-red-500/20 text-red-300' },
            { n: 'História', sigla: 'HIS', color: 'bg-amber-500/20 text-amber-300' },
            { n: 'Geografia', sigla: 'GEO', color: 'bg-purple-500/20 text-purple-300' },
            { n: 'Biologia', sigla: 'BIO', color: 'bg-emerald-500/20 text-emerald-300' },
            { n: 'Física', sigla: 'FIS', color: 'bg-cyan-500/20 text-cyan-300' },
          ].map((m, i) => (
            <div key={i} className={`p-2.5 border border-white/10 rounded-xl flex flex-col items-center justify-center text-center gap-1 bg-white/5`}>
              <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-md ${m.color}`}>{m.sigla}</span>
              <span className="text-[9px] font-bold mt-1 text-white/80 truncate w-full">{m.n}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (stepId === 'professores') {
    return (
      <div className="flex flex-col gap-2.5 h-full text-white/90 text-xs select-none">
        <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 border-b border-white/10 pb-1">
          Corpo Docente
        </div>
        <div className="flex flex-col gap-2">
          {[
            { nome: 'Prof. Marcos Silva', materias: ['Matemática', 'Física'], init: 'MS' },
            { nome: 'Profa. Ana Costa', materias: ['Português', 'Inglês'], init: 'AC' },
            { nome: 'Prof. Ricardo Santos', materias: ['Química'], init: 'RS' },
          ].map((p, i) => (
            <div key={i} className="p-2.5 bg-white/5 border border-white/10 rounded-xl flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-indigo-500/20 text-indigo-300 font-bold flex items-center justify-center text-[9px]">
                  {p.init}
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-[11px]">{p.nome}</span>
                  <span className="text-[9px] text-white/40">Professor(a)</span>
                </div>
              </div>
              <div className="flex gap-1">
                {p.materias.map((m, j) => (
                  <span key={j} className="text-[8px] bg-white/10 px-1.5 py-0.5 rounded text-white/80 font-bold">{m}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (stepId === 'turmas') {
    return (
      <div className="flex flex-col gap-3 h-full text-white/90 text-xs select-none">
        <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 border-b border-white/10 pb-1">
          Turmas do Ano Letivo
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {[
            { sala: '1º Ano A', desc: 'Ensino Médio', turno: 'Manhã' },
            { sala: '2º Ano B', desc: 'Ensino Médio', turno: 'Manhã' },
            { sala: '9º Ano A', desc: 'Ensino Fundamental', turno: 'Tarde' },
            { sala: '8º Ano B', desc: 'Ensino Fundamental', turno: 'Tarde' },
          ].map((t, i) => (
            <div key={i} className="p-2.5 bg-white/5 border border-white/10 rounded-xl flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="font-bold text-[11px]">{t.sala}</span>
                <span className="text-[8px] bg-blue-500/20 text-blue-300 px-1 rounded font-bold">{t.turno}</span>
              </div>
              <span className="text-[9px] text-white/40 leading-none">{t.desc}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (stepId === 'responsaveis') {
    return (
      <div className="flex flex-col gap-3 h-full text-white/90 text-xs select-none">
        <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 border-b border-white/10 pb-1">
          Vínculos de Responsáveis
        </div>
        <div className="flex flex-col gap-2 relative">
          <div className="flex justify-between items-center bg-white/5 border border-white/10 p-2 rounded-xl relative z-10">
            <div className="flex flex-col">
              <span className="font-bold text-[10px]">Maria Souza</span>
              <span className="text-[8px] text-white/40">Mãe / Responsável</span>
            </div>
            <div className="w-12 h-px border-t border-dashed border-white/30" />
            <div className="flex flex-col text-right">
              <span className="font-bold text-[10px]">Pedro Souza</span>
              <span className="text-[8px] text-white/40">Aluno - 1º Ano A</span>
            </div>
          </div>
          <div className="flex justify-between items-center bg-white/5 border border-white/10 p-2 rounded-xl relative z-10">
            <div className="flex flex-col">
              <span className="font-bold text-[10px]">João Lima</span>
              <span className="text-[8px] text-white/40">Pai / Responsável</span>
            </div>
            <div className="w-12 h-px border-t border-dashed border-white/30" />
            <div className="flex flex-col text-right">
              <span className="font-bold text-[10px]">Alice Lima</span>
              <span className="text-[8px] text-white/40">Aluna - 2º Ano B</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stepId === 'alunos') {
    return (
      <div className="flex flex-col gap-2.5 h-full text-white/90 text-xs select-none">
        <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 border-b border-white/10 pb-1">
          Matrícula de Alunos
        </div>
        <div className="flex flex-col gap-2">
          {[
            { nome: 'Pedro Souza', turma: '1º Ano A', status: 'Matriculado' },
            { nome: 'Alice Lima', turma: '2º Ano B', status: 'Matriculado' },
            { nome: 'Gabriel Silva', turma: '9º Ano A', status: 'Pendente' },
          ].map((a, i) => (
            <div key={i} className="p-2 bg-white/5 border border-white/10 rounded-xl flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-emerald-500/20 text-emerald-300 font-bold flex items-center justify-center text-[9px]">
                  {a.nome.split(' ').map(x => x[0]).join('')}
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-[11px]">{a.nome}</span>
                  <span className="text-[9px] text-white/40">{a.turma}</span>
                </div>
              </div>
              <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${a.status === 'Matriculado' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                {a.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (stepId === 'grade') {
    return (
      <div className="flex flex-col gap-2 h-full text-white/90 text-xs select-none">
        <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 border-b border-white/10 pb-1">
          Grade Horária Semanal
        </div>
        {/* Micro Horario Grid */}
        <div className="grid grid-cols-6 gap-1 mt-1 text-[8px] font-bold text-center">
          <div className="text-white/40">Hora</div>
          <div>SEG</div>
          <div>TER</div>
          <div>QUA</div>
          <div>QUI</div>
          <div>SEX</div>

          <div className="text-white/40 py-1 border-b border-white/5">07:00</div>
          <div className="bg-blue-500/20 text-blue-300 py-1 rounded">MAT</div>
          <div className="bg-red-500/20 text-red-300 py-1 rounded">PORT</div>
          <div className="bg-blue-500/20 text-blue-300 py-1 rounded">MAT</div>
          <div className="bg-purple-500/20 text-purple-300 py-1 rounded">GEO</div>
          <div className="bg-cyan-500/20 text-cyan-300 py-1 rounded">FIS</div>

          <div className="text-white/40 py-1">07:50</div>
          <div className="bg-cyan-500/20 text-cyan-300 py-1 rounded">FIS</div>
          <div className="bg-amber-500/20 text-amber-300 py-1 rounded">HIS</div>
          <div className="bg-red-500/20 text-red-300 py-1 rounded">PORT</div>
          <div className="bg-emerald-500/20 text-emerald-300 py-1 rounded">BIO</div>
          <div className="bg-blue-500/20 text-blue-300 py-1 rounded">MAT</div>
        </div>
      </div>
    );
  }

  return null;
}

function ActualPageRenderer({
  stepId,
  onConcluirGrade,
}: {
  stepId: string;
  onConcluirGrade?: () => void;
}) {
  switch (stepId) {
    case 'equipe':
      return <EquipePage />;
    case 'turnos':
      return <TurnosPage />;
    case 'materias':
      return <MateriasPage />;
    case 'professores':
      return <ProfessoresPage />;
    case 'turmas':
      return <TurmasPage />;
    case 'responsaveis':
      return <ResponsaveisPage />;
    case 'alunos':
      return <AlunosPage />;
    case 'grade':
      return (
        <GradeAssistant
          open={true}
          embedded={true}
          onClose={() => onConcluirGrade?.()}
        />
      );
    default:
      return <EquipePage />;
  }
}

function SetupConclusaoCard({
  nomeEscola,
  onNavigateDashboard,
}: {
  nomeEscola: string;
  onNavigateDashboard: () => void;
}) {
  const [countdown, setCountdown] = useState(5);
  const escolaId = useAuthStore((s) => s.user?.escolaId);
  const { confirmar: apiConfirmar } = useWizard();

  useEffect(() => {
    const finalizarBackend = async () => {
      try {
        if (escolaId) {
          try {
            await api.patch(`/escolas/${escolaId}/modo-implantacao`, { ativo: false });
          } catch (patchErr) {
            console.warn('[SetupWizard] Erro ao alternar modo-implantacao:', patchErr);
          }
          localStorage.setItem(`ac_wizard_concluido_${escolaId}`, 'true');
        }
        await apiConfirmar({});
        window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      } catch (err) {
        console.warn('[SetupWizard] Erro ao registrar conclusao do wizard:', err);
      }
    };
    finalizarBackend();
  }, [escolaId, apiConfirmar]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onNavigateDashboard();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onNavigateDashboard]);

  const confettiColors = ['#22c55e', '#eab308', '#3b82f6', '#ec4899', '#f97316', '#8b5cf6'];

  return (
    <div className="setup-wizard sw-conclusao-wrapper">
      <div className="sw-conclusao sw-conclusao--elevated">
        <div className="sw-confetti">
          {Array.from({ length: 28 }).map((_, i) => (
            <div
              key={i}
              className="sw-confetti-particle"
              style={{
                left: `${Math.random() * 100}%`,
                background: confettiColors[i % confettiColors.length],
                animationDelay: `${Math.random() * 2}s`,
                width: `${6 + Math.random() * 6}px`,
                height: `${6 + Math.random() * 6}px`,
                borderRadius: Math.random() > 0.5 ? '50%' : '2px',
              }}
            />
          ))}
        </div>

        {/* Ícone Grande de Sucesso no Topo */}
        <div className="sw-conclusao-icon-badge">
          <PartyPopper size={42} className="sw-conclusao-icon" />
        </div>

        <h2 className="sw-conclusao-title">
          Parabéns! <span>{nomeEscola}</span> está pronta para operar!
        </h2>

        <p className="sw-conclusao-text">
          Menu completo liberado com sucesso. Todas as etapas de configuração inicial foram concluídas.
        </p>

        {/* Barra de Progresso do Timer e Ação */}
        <div className="sw-conclusao-timer-box">
          <div className="sw-conclusao-timer-track">
            <div
              className="sw-conclusao-timer-fill"
              style={{ width: `${((5 - countdown) / 5) * 100}%` }}
            />
          </div>
          <p className="sw-conclusao-timer-text">
            Redirecionando para o Painel em <strong>{countdown}s</strong>...
          </p>
          <button
            type="button"
            className="sw-conclusao-btn"
            onClick={onNavigateDashboard}
          >
            Acessar Painel Agora <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

interface SetupWizardProps {
  routePrefix?: string;
  cargo?: string;
  welcomeTitle?: string;
  compact?: boolean;
}

export function SetupWizard({
  routePrefix = '/admin',
  cargo,
  welcomeTitle,
  compact = false,
}: SetupWizardProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const nomeEscola = useAuthStore((s) => s.nomeEscola) || 'sua escola';
  const {
    progresso,
    prontoParaOperar,
    etapas,
    etapasConcluidas,
    totalEtapas,
    isLoading,
    isFirstSetup,
    counts,
  } = useSetupStatus({ routePrefix, cargo });

  const { confirmar: apiConfirmar } = useWizard();
  const [isFinishing, setIsFinishing] = useState(false);
  const escolaId = useAuthStore((s) => s.user?.escolaId);

  const concluirWizard = async () => {
    setIsFinishing(true);
    try {
      if (escolaId) {
        try {
          await api.patch(`/escolas/${escolaId}/modo-implantacao`, { ativo: false });
        } catch (patchErr) {
          console.warn('[SetupWizard] Erro ao alternar modo-implantacao no backend:', patchErr);
        }
      }
      await apiConfirmar({});
      const storageKey = escolaId ? `ac_wizard_concluido_${escolaId}` : null;
      if (storageKey) {
        localStorage.setItem(storageKey, 'true');
      }
      toast.success('Parabéns! Sua escola está pronta para operar!');
      navigate(routePrefix === '/equipe' ? '/equipe/dashboard' : '/admin/dashboard', { replace: true });
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Erro ao concluir o wizard de setup.');
    } finally {
      setIsFinishing(false);
    }
  };

  const proximaEtapa = etapas.find((e) => e.status === 'pendente' || e.status === 'parcial');
  const focusIndex = proximaEtapa ? etapas.findIndex((e) => e.id === proximaEtapa.id) : 0;

  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const [isZoomed, setIsZoomed] = useState<boolean>(true);
  const [isLive, setIsLive] = useState<boolean>(true);
  const [hasInitializedFocus, setHasInitializedFocus] = useState(false);
  const [confirmSkipOpen, setConfirmSkipOpen] = useState(false);

  // Controle de tela pequena / Handoff para Computador
  const [isMobileScreen, setIsMobileScreen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 1024;
  });

  const [mobileBypass, setMobileBypass] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem('ac_setup_mobile_bypass') === 'true';
  });

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileScreen(window.innerWidth < 1024);
    };
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleMobileBypass = () => {
    try {
      sessionStorage.setItem('ac_setup_mobile_bypass', 'true');
    } catch {}
    setMobileBypass(true);
  };



  // Sincronizar via ?step= query param (links do sidebar durante setup)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stepParam = params.get('step');
    if (stepParam) {
      const idx = etapas.findIndex((e) => e.id === stepParam);
      if (idx !== -1) {
        setActiveStepIndex(idx);
        setIsLive(true);
        setIsZoomed(true);
        setHasInitializedFocus(true);
      }
    }
  }, [location.search, etapas]);

  // Sincronizar o activeStepIndex com a rota atual do navegador (se o filho redirecionar)
  useEffect(() => {
    const stepId = ADMIN_SETUP_PATH_TO_ETAPA[location.pathname];
    if (stepId) {
      const idx = etapas.findIndex((e) => e.id === stepId);
      if (idx !== -1 && idx !== activeStepIndex) {
        setActiveStepIndex(idx);
        setIsLive(true);
        setIsZoomed(true);
        setHasInitializedFocus(true);
      }
    }
  }, [location.pathname, etapas, activeStepIndex]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stepParam = params.get('step');
    if (stepParam) {
      setHasInitializedFocus(true);
      return;
    }
    const stepId = ADMIN_SETUP_PATH_TO_ETAPA[location.pathname];
    if (stepId) {
      setHasInitializedFocus(true);
      return;
    }
    if (focusIndex !== undefined && (!isLive || !hasInitializedFocus)) {
      setActiveStepIndex(focusIndex);
      setHasInitializedFocus(true);
    }
  }, [focusIndex, isLive, hasInitializedFocus, location.pathname, location.search]);

  useEffect(() => {
    if (!proximaEtapa) return;
    const t = window.setTimeout(() => {
      document.querySelector('.sw-deck-card--focus')?.scrollIntoView({
        inline: 'center',
        behavior: 'smooth',
        block: 'nearest',
      });
    }, 120);
    return () => window.clearTimeout(t);
  }, [proximaEtapa?.id]);

  if (isLoading && etapas.length === 0) {
    return (
      <div className="setup-wizard setup-wizard--loading">
        <p>Carregando trilha…</p>
      </div>
    );
  }

  if (prontoParaOperar) {
    return (
      <SetupConclusaoCard
        nomeEscola={nomeEscola}
        onNavigateDashboard={() => {
          navigate('/admin/dashboard');
        }}
      />
    );
  }

  const activeEtapa = etapas[activeStepIndex] || proximaEtapa || etapas[0];
  if (!activeEtapa) return null;

  const ActiveIcon = ICONE_MAP[activeEtapa.iconeKey] || BookOpen;
  const resumoBase = ETAPA_RESUMO[activeEtapa.id] || ETAPA_RESUMO.equipe;
  const resumo = { ...resumoBase };
  if (activeEtapa.id === 'equipe') {
    if (activeEtapa.status === 'concluida') {
      resumo.titulo = 'Equipe configurada!';
      resumo.linha = 'Sua equipe administrativa inicial foi cadastrada. Você já pode avançar ou cadastrar mais membros.';
      resumo.btn = 'Adicionar outro membro';
    }
  } else if (activeEtapa.status === 'concluida') {
    resumo.titulo = `${activeEtapa.nome} configurado(a)!`;
    resumo.linha = 'Esta etapa já foi concluída com sucesso.';
    resumo.btn = `Visualizar ${activeEtapa.nome.toLowerCase()}`;
  }

  const abrirPreview = () => {
    if (!isZoomed) {
      setIsZoomed(true);
      setTimeout(() => {
        setIsLive(true);
      }, 500);
    } else if (!isLive) {
      setIsLive(true);
    }
  };

  const goProxima = () => {
    if (activeEtapa.id === 'grade') {
      concluirWizard();
      return;
    }
    // Se a etapa ativa já estiver concluída, clicar no botão principal abre a visualização
    if (activeEtapa.status === 'concluida') {
      abrirPreview();
      return;
    }

    const clickTargetButton = (pane: HTMLElement) => {
      const buttons = Array.from(pane.querySelectorAll('button'));
      let actionBtn: HTMLButtonElement | undefined;
      
      if (activeEtapa.id === 'turmas') {
        actionBtn = buttons.find(b => {
          const txt = b.textContent?.toLowerCase() || '';
          return txt.includes('assistente') || txt.includes('lote');
        });
      }
      
      if (!actionBtn) {
        actionBtn = buttons.find(b => {
          const txt = b.textContent?.toLowerCase() || '';
          return txt.includes('adicionar') || txt.includes('configurar') || txt.includes('cadastrar') || txt.includes('criar') || txt.includes('montar') || txt.includes('novo') || b.className.includes('btn-primary');
        });
      }
      
      if (actionBtn) {
        actionBtn.click();
      }
    };

    if (!isZoomed) {
      setIsZoomed(true);
      // Primeiro faz o zoom no preview (500ms) e só depois renderiza a página real funcional
      setTimeout(() => {
        setIsLive(true);
        // Pequena folga para montagem do componente real antes de clicar no botão
        setTimeout(() => {
          const rightPane = document.getElementById('sw-actual-page-container');
          if (rightPane) {
            clickTargetButton(rightPane);
          }
        }, 150);
      }, 500);
    } else if (!isLive) {
      setIsLive(true);
      setTimeout(() => {
        const rightPane = document.getElementById('sw-actual-page-container');
        if (rightPane) {
          clickTargetButton(rightPane);
        }
      }, 150);
    } else {
      const rightPane = document.getElementById('sw-actual-page-container');
      if (rightPane) {
        clickTargetButton(rightPane);
      }
    }
  };

  const eyebrowText = activeStepIndex === focusIndex ? 'Próxima Ação' : 'Etapa de Implantação';

  const openEtapa = (etapa: EtapaSetup) => {
    if (etapa.status === 'bloqueada') {
      toast.info(
        etapa.bloqueadoPor
          ? `Conclua "${etapa.bloqueadoPor}" antes.`
          : 'Conclua a etapa anterior primeiro.',
      );
      return;
    }
    setActiveStepIndex(etapas.findIndex((e) => e.id === etapa.id));
    setIsLive(true);
    setIsZoomed(true);
  };

  if (isMobileScreen && !mobileBypass) {
    return (
      <SetupMobileHandoff
        onBypass={handleMobileBypass}
        escolaNome={nomeEscola}
        progressoGeral={Math.round(progresso || 0)}
      />
    );
  }

  return (
    <div 
      className="setup-wizard setup-wizard--premium w-full h-full flex flex-col bg-transparent"
      data-testid="setup-wizard"
      data-lab-active-step={activeEtapa?.id || ''}
      data-lab-step-status={activeEtapa?.status || ''}
      style={{
        width: '100%',
        height: '100%',
        flexShrink: 0,
        background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 42%, #3b82f6 100%)',
      }}
    >
      {/* Top Progress bar */}
      <div className="w-full bg-blue-950/30 px-6 md:px-8 py-2.5 md:py-3 border-b border-white/10 flex items-center justify-between select-none shrink-0 z-20 relative backdrop-blur-md">
        <div className="flex items-center gap-2.5">
          {/* Botão de Toggle da Sidebar do Setup */}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('toggle-setup-sidebar'))}
            className="group p-1.5 md:p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg border border-white/10 backdrop-blur-md flex items-center justify-center gap-1.5 transition-all duration-200 active:scale-95 shadow-md mr-1.5"
            title="Menu de Implantação"
          >
            <Menu size={14} />
            <span className="text-[10px] font-bold uppercase tracking-wider hidden sm:inline">Implantação</span>
          </button>
          
          <span className="text-[11px] md:text-xs font-bold text-blue-200/70 uppercase tracking-widest hidden md:inline">Progresso Geral da Escola</span>
          <span className="text-[11px] font-bold text-blue-200/70 uppercase tracking-widest md:hidden">Progresso da Escola</span>
          <span className="text-xs md:text-sm font-black text-white bg-blue-500/20 border border-blue-500/25 px-2 py-0.5 rounded-lg shadow-inner">
            {Math.round(progresso || 0)}%
          </span>
        </div>
        <div className="flex-1 max-w-xs md:max-w-md h-2 bg-white/10 rounded-full overflow-hidden ml-4 md:ml-6 border border-white/5 shadow-inner relative">
          <div 
            className="h-full bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full transition-all duration-700 shadow-[0_0_12px_rgba(129,140,248,0.6)]" 
            style={{ width: `${progresso}%` }} 
          />
        </div>

        <div className="flex items-center gap-3 ml-4 md:ml-6">
          <button
            type="button"
            onClick={() => setConfirmSkipOpen(true)}
            className="group px-3 py-1.5 md:px-3.5 bg-white hover:bg-blue-50 text-blue-700 hover:text-blue-800 rounded-lg shadow-md hover:shadow-lg flex items-center justify-center gap-1.5 transition-all duration-200 active:scale-95 text-xs font-black border border-white/80 shrink-0"
            title="Pular Guia e entrar direto no sistema"
          >
            <span className="hidden md:inline">Pular Guia!</span>
            <span className="md:hidden">PULAR</span>
            <CornerUpRight size={15} className="sw-skip-btn-icon text-blue-600 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>

      <div 
        className="w-full flex-1 text-white relative overflow-hidden flex flex-col md:flex-row gap-2 md:gap-8 lg:gap-10 items-center justify-between select-none min-h-0 px-2 py-1.5 sm:px-3 sm:py-2 md:p-6 lg:p-8"
      >
        {/* Content Side - Compacto no mobile, amplo no desktop */}
        <div className="shrink-0 md:flex-1 z-10 flex flex-col items-start text-left gap-1 md:gap-4 max-w-2xl md:h-full md:justify-between pt-0 min-h-0 w-full">
          {/* Header da Etapa: Ícone + Título/Subtítulo no mobile; Ícone + Eyebrow no desktop */}
          <div className="flex items-start gap-2.5 sm:gap-3 w-full shrink-0">
            <div 
              className="bg-white/10 rounded-xl md:rounded-2xl border border-white/20 backdrop-blur-md shadow-inner flex items-center justify-center shrink-0 w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 mt-0.5 md:mt-0"
            >
              <ActiveIcon size={22} strokeWidth={1.5} className="text-white drop-shadow-[0_4px_12px_rgba(255,255,255,0.3)] animate-pulse md:w-7 md:h-7 lg:w-8 lg:h-8" />
            </div>
            
            {/* Desktop Eyebrow */}
            <div className="hidden md:flex flex-col">
              <span className="text-xs md:text-sm lg:text-base font-extrabold uppercase tracking-widest text-blue-100/90 leading-none">
                {eyebrowText}
              </span>
            </div>

            {/* Mobile Title + Subtitle (Alinhados na frente do ícone) */}
            <div className="flex md:hidden flex-col flex-1 min-w-0">
              <h1 className="text-base sm:text-lg font-black tracking-tight leading-tight text-white truncate">
                {resumo.titulo}
              </h1>
              <div className="text-xs sm:text-sm text-blue-100/95 leading-snug font-medium line-clamp-2 mt-0.5">
                {resumo.linha}
              </div>
            </div>
          </div>
 
          {/* Desktop Title & Subtitle */}
          <div className="hidden md:flex flex-col gap-2 md:gap-3 shrink-0">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-tight md:leading-[1.1]">
              {resumo.titulo}
            </h1>
            <div className="text-sm sm:text-base md:text-lg text-blue-100/95 leading-snug font-medium max-w-xl">
              {resumo.linha}
            </div>
          </div>
 
          {/* Botões CTA - Visíveis SOMENTE no Desktop (ocultos em modo mobile) */}
          {activeEtapa.status === 'concluida' || activeEtapa.id === 'grade' ? (
            <div className="hidden md:flex w-full flex-col gap-2.5 max-w-md shrink-0">
              <button
                type="button"
                data-testid="btn-wizard-next-step"
                data-lab-action="wizard-next-step"
                data-lab-safe-action="true"
                disabled={isFinishing}
                onClick={() => {
                  if (activeStepIndex < totalEtapas - 1 && activeEtapa.id !== 'grade') {
                    setActiveStepIndex(activeStepIndex + 1);
                    setIsLive(true);
                    setIsZoomed(true);
                  } else {
                    concluirWizard();
                  }
                }}
                className="sw-btn-shimmer w-full py-3.5 md:py-4 rounded-xl md:rounded-2xl font-black text-base md:text-lg flex items-center justify-center gap-2.5 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isFinishing ? (
                  'Concluindo...'
                ) : (activeStepIndex === totalEtapas - 1 || activeEtapa.id === 'grade') ? (
                  <>Concluir implantação <CheckCircle2 size={20} /></>
                ) : (
                  <>Ir para próximo passo <ArrowRight size={20} /></>
                )}
              </button>
            </div>
          ) : (
            <button 
              type="button" 
              data-testid="btn-wizard-step-cta"
              data-lab-action="wizard-step-cta"
              onClick={goProxima}
              className="hidden md:flex group px-6 py-3 md:px-8 md:py-3.5 bg-white text-blue-700 hover:text-blue-800 rounded-xl md:rounded-2xl font-black text-sm md:text-base lg:text-lg items-center gap-3 shadow-lg shadow-blue-900/35 hover:shadow-xl hover:shadow-blue-900/40 hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150 shrink-0"
            >
              {activeEtapa.id === 'grade' ? 'Concluir' : resumo.btn}
              <ArrowRight size={20} className="group-hover:translate-x-1.5 transition-transform" />
            </button>
          )}

          {/* Controls Bar no Desktop (na base da coluna esquerda) */}
          <div className="hidden md:flex mt-auto pt-3 md:pt-4 items-center justify-between w-full border-t border-white/10 gap-3 select-none shrink-0">
            <button
              type="button"
              onClick={() => {
                if (activeStepIndex > 0) {
                  setActiveStepIndex(activeStepIndex - 1);
                  setIsLive(true);
                  setIsZoomed(true);
                }
              }}
              disabled={activeStepIndex === 0}
              className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg md:rounded-xl font-bold text-xs flex items-center gap-1.5 border border-white/20 transition-all ${
                activeStepIndex === 0
                  ? 'opacity-20 cursor-not-allowed text-white/50'
                  : 'bg-white/10 hover:bg-white/20 text-white active:scale-95'
              }`}
            >
              <ChevronLeft size={14} />
              <span>Anterior</span>
            </button>

            {/* Steps indicator dots */}
            <div className="flex gap-2">
              {etapas.map((etapa, idx) => {
                const isCurrent = idx === activeStepIndex;
                const isDone = etapa.status === 'concluida';
                const isLocked = etapa.status === 'bloqueada';

                return (
                  <button
                    key={etapa.id}
                    onClick={() => {
                      if (!isLocked) {
                        setActiveStepIndex(idx);
                        setIsLive(true);
                        setIsZoomed(true);
                      }
                    }}
                    disabled={isLocked}
                    className={`w-2 h-2 md:w-2.5 md:h-2.5 rounded-full transition-all duration-200 ${
                      isCurrent
                        ? 'bg-white scale-125 shadow-[0_0_8px_rgba(255,255,255,0.8)]'
                        : isDone
                        ? 'bg-emerald-400 hover:bg-emerald-300'
                        : isLocked
                        ? 'bg-white/10 cursor-not-allowed'
                        : 'bg-white/40 hover:bg-white/60'
                    }`}
                    title={etapa.nome}
                  />
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => {
                if (activeStepIndex < totalEtapas - 1 && activeEtapa.id !== 'grade') {
                  setActiveStepIndex(activeStepIndex + 1);
                  setIsLive(true);
                  setIsZoomed(true);
                } else {
                  concluirWizard();
                }
              }}
              disabled={(activeEtapa.status !== 'concluida' && activeEtapa.id !== 'grade') || isFinishing}
              className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg md:rounded-xl font-bold text-xs flex items-center gap-1.5 border border-white/20 transition-all ${
                (activeEtapa.status !== 'concluida' && activeEtapa.id !== 'grade') || isFinishing
                  ? 'opacity-20 cursor-not-allowed text-white/50 bg-transparent'
                  : 'bg-white/10 hover:bg-white/20 text-white active:scale-95'
              }`}
            >
              <span>{activeStepIndex === totalEtapas - 1 || activeEtapa.id === 'grade' ? 'Concluir' : 'Avançar'}</span>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>

        {/* Right Side - Interactive Browser Mockup / Live Page (Expande para 100% da altura no mobile) */}
        <div 
          className="flex-1 z-10 flex flex-col items-center justify-center p-0 min-w-0 self-stretch w-full h-full min-h-0"
          style={{
            flex: isZoomed ? '1.8 1 0%' : '1 1 0%',
            maxWidth: isZoomed ? '100%' : '680px',
            height: '100%',
            width: '100%',
            transition: 'flex 0.5s ease-in-out, max-width 0.5s ease-in-out'
          }}
        >
          <div className={`w-full h-full bg-slate-950/40 rounded-xl md:rounded-2xl border backdrop-blur-md shadow-2xl overflow-hidden flex flex-col transition-all duration-500 ease-in-out ${
            isZoomed 
              ? 'border-white/30 shadow-[0_0_30px_rgba(255,255,255,0.15)]' 
              : 'border-white/10 hover:border-white/15'
          }`}>
            {/* Browser Header Bar */}
            <div className="bg-slate-950/60 px-3 py-1.5 md:px-4 md:py-3 border-b border-white/5 flex items-center justify-between select-none shrink-0">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="flex gap-1.5">
                  <button 
                    type="button" 
                    className="w-2.5 h-2.5 rounded-full bg-red-500/80 cursor-default" 
                  />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                  <button 
                    type="button" 
                    onClick={() => window.open(`/admin/${activeEtapa.id}`, '_blank')}
                    className="w-2.5 h-2.5 rounded-full bg-green-500/80 hover:bg-green-400 active:scale-90 transition-all cursor-pointer" 
                    title="Abrir em tela cheia (nova aba)"
                  />
                </div>
                <span className="text-[10px] text-white/60 font-semibold tracking-wide hidden sm:inline-block ml-1">
                  {MOCK_BROWSER_TITLES[activeEtapa.id] || 'Painel Administrativo'}
                </span>
              </div>
              <div 
                onClick={() => window.open(`/admin/${activeEtapa.id}`, '_blank')}
                className="bg-white/5 border border-white/5 hover:bg-white/10 hover:border-white/10 cursor-pointer transition-all rounded-lg px-3 py-0.5 text-[9px] text-white/40 font-mono tracking-wide max-w-[180px] sm:max-w-xs md:w-64 truncate text-center select-none flex items-center justify-center gap-1"
                title="Clique para abrir esta tela em nova aba"
              >
                <span>alunoconectado.app/admin/{activeEtapa.id}</span>
                <span className="text-[7px]">↗</span>
              </div>
              <div className="w-6 md:w-20" />
            </div>

            {/* Content Switcher */}
            <div className="flex-1 flex flex-col overflow-hidden relative">
              {isLive ? (
                <div id="sw-actual-page-container" className="sw-dashboard-actual-page flex-1 overflow-hidden flex flex-col relative text-slate-800 bg-slate-50 transition-all duration-300">
                  <ActualPageRenderer stepId={activeEtapa.id} onConcluirGrade={concluirWizard} />
                </div>
              ) : (
                <div 
                  onClick={goProxima}
                  className="flex-1 p-6 overflow-hidden relative bg-slate-900/10 hover:bg-white/5 cursor-pointer transition-all duration-300"
                  title="Clique para abrir e configurar"
                >
                  <MockupContent stepId={activeEtapa.id} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation Bar (Lá em baixo da janela) */}
      <div className="md:hidden w-full bg-blue-950/60 backdrop-blur-md border-t border-white/10 px-4 py-2.5 flex items-center justify-between gap-2 shrink-0 select-none z-20">
        <button
          type="button"
          onClick={() => {
            if (activeStepIndex > 0) {
              setActiveStepIndex(activeStepIndex - 1);
              setIsLive(true);
              setIsZoomed(true);
            }
          }}
          disabled={activeStepIndex === 0}
          className={`px-3 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1 border border-white/20 transition-all ${
            activeStepIndex === 0
              ? 'opacity-20 cursor-not-allowed text-white/50'
              : 'bg-white/10 hover:bg-white/20 text-white active:scale-95'
          }`}
        >
          <ChevronLeft size={14} />
          <span>Anterior</span>
        </button>

        {/* Steps indicator dots */}
        <div className="flex gap-1.5 items-center">
          {etapas.map((etapa, idx) => {
            const isCurrent = idx === activeStepIndex;
            const isDone = etapa.status === 'concluida';
            const isLocked = etapa.status === 'bloqueada';

            return (
              <button
                key={etapa.id}
                onClick={() => {
                  if (!isLocked) {
                    setActiveStepIndex(idx);
                    setIsLive(true);
                    setIsZoomed(true);
                  }
                }}
                disabled={isLocked}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  isCurrent
                    ? 'bg-white scale-125 shadow-[0_0_6px_rgba(255,255,255,0.9)]'
                    : isDone
                    ? 'bg-emerald-400'
                    : isLocked
                    ? 'bg-white/10 cursor-not-allowed'
                    : 'bg-white/40'
                }`}
                title={etapa.nome}
              />
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => {
            if (activeStepIndex < totalEtapas - 1 && activeEtapa.id !== 'grade') {
              setActiveStepIndex(activeStepIndex + 1);
              setIsLive(true);
              setIsZoomed(true);
            } else {
              concluirWizard();
            }
          }}
          disabled={(activeEtapa.status !== 'concluida' && activeEtapa.id !== 'grade') || isFinishing}
          className={`px-3 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1 border border-white/20 transition-all ${
            (activeEtapa.status !== 'concluida' && activeEtapa.id !== 'grade') || isFinishing
              ? 'opacity-20 cursor-not-allowed text-white/50 bg-transparent'
              : 'bg-white/10 hover:bg-white/20 text-white active:scale-95'
          }`}
        >
          <span>{activeStepIndex === totalEtapas - 1 || activeEtapa.id === 'grade' ? 'Concluir' : 'Avançar'}</span>
          <ChevronRight size={14} />
        </button>
      </div>

      <ConfirmModal
        open={confirmSkipOpen}
        title="Pular Guia de Implantação?"
        message="Deseja entrar direto no sistema sem continuar o guia de implantação? Você poderá retornar ao painel de setup a qualquer momento."
        confirmLabel="Entrar no Sistema"
        cancelLabel="Cancelar"
        variant="default"
        onCancel={() => setConfirmSkipOpen(false)}
        onConfirm={() => {
          setConfirmSkipOpen(false);
          const storageKey = escolaId ? `ac_wizard_concluido_${escolaId}` : null;
          if (storageKey) {
            localStorage.setItem(storageKey, 'true');
          }
          navigate(routePrefix === '/equipe' ? '/equipe/dashboard' : '/admin/dashboard');
        }}
      />
    </div>
  );
}
